import React from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface JobSearchStats {
  applicationCount: number;
  responseRate: number;
  mostCommonRoles: string[];
  averageResponseTime: number;
}

interface GuidanceProps {
  stats: JobSearchStats;
}

const JobSearchGuidance: React.FC<GuidanceProps> = ({ stats }) => {
  const generateRecommendations = () => {
    const recommendations = [];

    if (stats.responseRate < 0.1) {
      recommendations.push({
        title: "Level Up Your Applications",
        icon: CheckCircleIcon,
        tips: [
          "Craft a compelling resume tailored to each position",
          "Write an engaging cover letter that tells your story",
          "Showcase impactful projects that demonstrate your expertise",
        ]
      });
    }

    if (stats.applicationCount < 10) {
      recommendations.push({
        title: "Accelerate Your Job Search",
        icon: ChartBarIcon,
        tips: [
          "Challenge yourself with ambitious weekly application targets",
          "Broaden your horizons by exploring adjacent roles",
          "Maximize visibility across multiple job platforms",
        ]
      });
    }

    return recommendations;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-8 space-y-8"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
          Your Strategic Job Search Compass
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-blue-50 rounded-xl p-6 flex flex-col space-y-2"
        >
          <ChartBarIcon className="w-8 h-8 text-blue-600" />
          <span className="text-sm text-gray-600">Your Monthly Progress</span>
          <span className="text-2xl font-bold text-blue-900">{stats.applicationCount} Applications</span>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-green-50 rounded-xl p-6 flex flex-col space-y-2"
        >
          <CheckCircleIcon className="w-8 h-8 text-green-600" />
          <span className="text-sm text-gray-600">Engagement Success</span>
          <span className="text-2xl font-bold text-green-900">{(stats.responseRate * 100).toFixed(1)}% Response Rate</span>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-purple-50 rounded-xl p-6 flex flex-col space-y-2"
        >
          <ClockIcon className="w-8 h-8 text-purple-600" />
          <span className="text-sm text-gray-600">Time to Response</span>
          <span className="text-2xl font-bold text-purple-900">{stats.averageResponseTime} Day Average</span>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {generateRecommendations().map((rec, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center space-x-3 mb-4">
              <rec.icon className="w-6 h-6 text-blue-600" />
              <h4 className="text-lg font-semibold text-gray-900">{rec.title}</h4>
            </div>
            <ul className="space-y-3">
              {rec.tips.map((tip, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: (index * 0.1) + (i * 0.1) }}
                  className="flex items-center space-x-2 text-gray-700"
                >
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                  <span>{tip}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default JobSearchGuidance;